import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { de_DE, en_US } from 'ng-zorro-antd/i18n';
import { UserSettings } from '../models/settings.model';
import { SupportedLanguage, supportLangCountry } from '../../public-api';
import { SettingsApi } from '../api/settings.api';
import { SettingsMapper } from '../../public-api';
import { SettingsUtil } from '../utilities/settings.util';

@Injectable({ providedIn: 'root' })
export class SettingsStore {
  private settingsApi = inject(SettingsApi);
  private mapper = inject(SettingsMapper);

  // ✅ Private mutable signals (only store can modify)
  private _settings = signal<UserSettings | null>(null);
  private _loading = signal(false);
  private _error = signal<string | null>(null);

  // ✅ Public readonly signals (components can only read)
  readonly settings = this._settings.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // ✅ Computed: User settings (for backward compatibility)
  readonly userSettings = computed(() => this._settings());

  // ✅ Computed: Locale ID
  readonly localeID = computed(() => {
    const settings = this._settings();
    if (!settings) return 'en-US';

    const lang = SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation;
    return lang === SupportedLanguage.DE
      ? supportLangCountry.deDE
      : lang === SupportedLanguage.PT
        ? supportLangCountry.ptPT
        : supportLangCountry.enUS;
  });

  // ✅ Computed: ng-zorro i18n
  readonly nzi18n = computed(() => {
    const settings = this._settings();
    if (!settings) return en_US;

    return SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation ===
      SupportedLanguage.DE
      ? de_DE
      : en_US;
  });

  // ✅ Computed: Is user from LATAM
  readonly isLatam = computed(() => {
    const settings = this._settings();
    return settings ? SettingsUtil.isLatam(settings.countryID ?? 0) : false;
  });

  // ✅ Computed: Current language
  readonly currentLanguage = computed(() => {
    const settings = this._settings();
    return settings ? SettingsUtil.getLanguage(settings.languages, settings.languageID) : undefined;
  });

  // ✅ Computed: Current theme
  readonly currentTheme = computed(() => {
    const settings = this._settings();
    return settings ? SettingsUtil.getTheme(settings.themes, settings.themeID) : undefined;
  });

  // ✅ Computed: Current date format
  readonly currentDateFormat = computed(() => {
    const settings = this._settings();
    return settings
      ? SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
      : undefined;
  });

  // ✅ Computed: Current measurement system
  readonly currentMeasurementSystem = computed(() => {
    const settings = this._settings();
    return settings
      ? SettingsUtil.getMeasurementSystem(settings.measurementSystems, settings.measurementSystemID)
      : undefined;
  });

  /**
   * ✅ Action: Load user settings from API
   */
  async loadSettings(): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const dto = await firstValueFrom(this.settingsApi.getUserSettings());
      const settings = this.mapper.toDomain(dto);
      this._settings.set(settings);
    } catch (error) {
      console.error('Failed to load user settings:', error);
      this._error.set(error instanceof Error ? error.message : 'Failed to load settings');
      this._settings.set(null);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * ✅ Action: Update user settings
   */
  async updateSettings(settings: UserSettings): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const dto = this.mapper.toDTO(settings);
      await firstValueFrom(this.settingsApi.updateUserSettings(dto));

      // API doesn't return response body, so update local state with the settings we sent
      // Normalize the settings to ensure consistency
      const normalizedSettings = this.mapper.toDomain(settings);
      this._settings.set(normalizedSettings);
    } catch (error) {
      console.error('Failed to update user settings:', error);
      this._error.set(error instanceof Error ? error.message : 'Failed to update settings');
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  clear(): void {
    this._settings.set(null);
    this._loading.set(false);
    this._error.set(null);
  }
}
